/**
 * Clinic portal long term frequent list hook.
 * API calls go through @/services/longTermFrequentService; errors surfaced via toast.
 */

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchLongTermFrequentList,
  type LongTermFrequentListQueryParams,
} from '@/services/longTermFrequentService';
import useAppToast from '@/hooks/useAppToast';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE } from '@/utils/apiError';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useClinicContext } from './useClinicContext';

export type LongTermFrequentListFilters = LongTermFrequentListQueryParams;

/**
 * Paginated long term frequent list for a patient (tenant + clinic from auth context).
 */
export const useLongTermFrequentList = (
  patientId?: string,
  filters?: LongTermFrequentListQueryParams,
  options?: { enabled?: boolean }
) => {
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const toast = useAppToast();

  const { data, isLoading, isFetching, isError, isPending, refetch, error } = useQuery({
    queryKey: [
      'long-term-frequents',
      'list',
      tenant,
      clinicNumber,
      patientId,
      serviceType,
      filters,
    ],
    queryFn: () => fetchLongTermFrequentList(tenant, clinicNumber, patientId!, filters),
    enabled: (options?.enabled ?? true) && !!tenant && !!clinicNumber && !!patientId,
    staleTime: 30_000,
    placeholderData: (previousData, previousQuery) => {
      const prevServiceType = previousQuery?.queryKey?.[5];
      return prevServiceType === serviceType ? previousData : undefined;
    },
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isFetching, isError, isPending, refetch, error };
};

export default useLongTermFrequentList;
